import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Container } from 'react-bootstrap';
import { Link } from 'react-router-dom';

function PrivacyPolicy() {
    useEffect(() => { window.scrollTo(0, 0); }, []);

    return (
        <main className="privacy-page">
            <Helmet>
                <title>Privacy Statement — SRL Advisor</title>
                <meta name="description" content="How SRL Advisor handles data on the website and platform. Pseudonymisation, consent, GDPR compliance, and data subject rights." />
                <link rel="canonical" href="https://srladvisor.com/privacy" />
            </Helmet>
            <Container className="privacy-container">
                <Link to="/" className="privacy-back">&larr; Back to home</Link>
                <h1 className="privacy-title">Privacy Statement</h1>
                <p className="privacy-effective">Effective date: July 2026</p>

                <section className="privacy-section">
                    <h2>Who we are</h2>
                    <p>
                        SRL Advisor is a doctoral research project at the University of
                        St&nbsp;Andrews, led by Andrew Schwabe in the School of Computer
                        Science. This privacy statement explains how we handle information
                        collected through the SRL Advisor website (srladvisor.com) and
                        the SRL Advisor platform (the Moodle plugin and its supporting
                        services).
                    </p>
                    <p>
                        For questions about this statement, contact{' '}
                        <a href="mailto:hello@srladvisor.com">hello@srladvisor.com</a>.
                    </p>
                </section>

                <section className="privacy-section">
                    <h2>What this statement covers</h2>
                    <p>
                        This statement covers two things: (1)&nbsp;the public website you
                        are on now, and (2)&nbsp;the SRL Advisor platform used by students
                        and educators at participating institutions. Each is addressed
                        separately below.
                    </p>
                </section>

                {/* ================================================================
                    PART 1 — WEBSITE
                    ================================================================ */}
                <section className="privacy-section">
                    <h2>Part 1 — The website (srladvisor.com)</h2>

                    <h3>Information we collect</h3>
                    <p>
                        When you use the contact form, we collect the information you
                        provide: your name, email address, institution, and message. This
                        information is submitted to HubSpot, our form-processing provider,
                        via their Forms API.
                    </p>
                    <p>
                        We use Google Analytics to understand how visitors use this
                        website — for example, which pages are visited and how users
                        navigate between them. Google Analytics uses cookies to collect
                        anonymised usage data. IP addresses are anonymised before
                        processing. We do not use this data to identify individual
                        visitors. Google's privacy policy is available at{' '}
                        <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                            policies.google.com/privacy
                        </a>.
                    </p>
                    <p>
                        We do not serve ads or use advertising trackers.
                    </p>

                    <h3>Lawful basis</h3>
                    <p>
                        We process contact-form submissions on the basis of legitimate
                        interest: responding to enquiries about the project and discussing
                        potential collaboration or pilot participation. Website analytics
                        are also processed on the basis of legitimate interest: understanding
                        how the website is used to improve its content and usability.
                    </p>

                    <h3>Third-party processors</h3>
                    <p>
                        <strong>HubSpot, Inc.</strong> processes contact form submissions
                        on our behalf. HubSpot's privacy policy is available at{' '}
                        <a href="https://legal.hubspot.com/privacy-policy" target="_blank" rel="noopener noreferrer">
                            legal.hubspot.com/privacy-policy
                        </a>. Data submitted via the contact form is stored in HubSpot's
                        North America (NA2) region.
                    </p>
                    <p>
                        <strong>Google LLC</strong> processes website analytics data on
                        our behalf via Google Analytics. Analytics data is processed in
                        accordance with Google's data processing terms.
                    </p>
                </section>

                {/* ================================================================
                    PART 2 — PLATFORM
                    ================================================================ */}
                <section className="privacy-section">
                    <h2>Part 2 — The SRL Advisor platform</h2>

                    <h3>Ethics and oversight</h3>
                    <p>
                        This research is conducted under the oversight of the University of
                        St&nbsp;Andrews and is subject to its ethics review process. Each
                        study in the research programme requires a separate ethics
                        application before any student data is collected. The data
                        management practices described here are governed by the project's
                        formal Data Management Plan, maintained on file with the University.
                    </p>

                    <h3>Consent and lawful basis</h3>
                    <p>
                        Participation is voluntary. The lawful basis for processing platform
                        data is explicit, informed consent (GDPR Article 6(1)(a)). Students
                        must opt in before any data is collected. Consent text is versioned;
                        every data record is linked to the consent version active at the
                        time of submission, creating a complete audit trail.
                    </p>
                    <p>
                        A student who declines is recorded as a permanent opt-out and is
                        not re-prompted. The SRL Advisor backend enforces this consent
                        gate: telemetry events and survey submissions from non-consented
                        students are rejected and never persisted.
                    </p>

                    <h3>Two-system data separation</h3>
                    <p>
                        The project employs a deliberate two-system architecture that
                        separates personally identifiable data from research data:
                    </p>
                    <ul>
                        <li>
                            <strong>Your institution's Moodle</strong> — all
                            personally identifiable information (student names, email
                            addresses, Moodle user IDs) remains exclusively on your
                            institution's hosted Moodle installation. The Moodle database
                            is the only system that holds the mapping between a student's
                            real identity and their pseudonymous research identifier.
                        </li>
                        <li>
                            <strong>External SRL Advisor platform</strong> — the SRL
                            Advisor backend receives and stores only pseudonymised data. No
                            email addresses, names, or raw Moodle user IDs are ever
                            collected by the SRL Advisor software.
                        </li>
                    </ul>

                    <h3>Pseudonymisation</h3>
                    <p>
                        When a student launches SRL Advisor from Moodle, the Moodle plugin
                        computes a one-way SHA-256 hash of the student's Moodle user ID
                        combined with the Moodle site's
                        unique <code>siteidentifier</code> salt. This hash becomes the
                        student's pseudonymous research identifier. No names, emails, or
                        raw Moodle user IDs appear in any data transmitted to the SRL
                        Advisor backend.
                    </p>
                    <p>
                        The re-identification mapping (the <code>siteidentifier</code> salt)
                        is held only by the institution's Moodle instance. It is not
                        shared with the researcher or the SRL Advisor platform.
                        Re-identification, if ever required (e.g., for a participant
                        withdrawal request), can only be performed by the institution's
                        Moodle administrator.
                    </p>

                    <h3>What we collect</h3>
                    <p>
                        Under pseudonymous identifiers, the platform collects the following
                        categories of data:
                    </p>
                    <ul>
                        <li>
                            <strong>Survey responses</strong> — validated self-regulated
                            learning instruments (baseline and summative surveys),
                            pre-unit strategy selection from a curated catalogue, and
                            post-unit reflections. Each record includes the prompt version
                            and response time in milliseconds as a data-quality signal.
                        </li>
                        <li>
                            <strong>Learning behaviour telemetry</strong> — passive signals
                            captured by JavaScript modules in the Moodle plugin, batched
                            every 5 seconds. These include scroll events (depth, speed,
                            direction), video interaction events (play, pause, seek, rate
                            changes, completion), download events (file type classification
                            only — no filenames, URLs, or file content), and clipboard
                            events (character count only — text content is never captured).
                        </li>
                        <li>
                            <strong>Moodle grade and completion data</strong> — read-only
                            grade records (raw scores only, no assignment content) and
                            activity-completion states, synced via a dedicated read-only
                            Moodle web-service account. The Moodle plugin actively strips
                            12 PII fields from every sync response before transmission.
                        </li>
                        <li>
                            <strong>Demographic data (optional, subject to ethics
                            approval)</strong> — if introduced in a future study,
                            self-report demographics may include age band, sex, first
                            language, country, prior exposure to subject, year of study, and
                            discipline. Every field will support a "prefer not to say"
                            option, and students may decline the entire demographic survey.
                        </li>
                    </ul>

                    <h3>What we do not collect</h3>
                    <p>
                        The platform does not collect student names, email addresses, IP
                        addresses, page content, video content, copied text, keystrokes,
                        mouse position, filenames, file content, or any other direct
                        personal identifier.
                    </p>

                    <h3>How we use this data</h3>
                    <p>
                        Data is used for two purposes: (1)&nbsp;to provide personalised
                        self-regulated learning feedback to students within the platform,
                        and (2)&nbsp;to support doctoral research on self-regulated learning
                        in higher education. Research findings are reported only in
                        aggregate or with sufficient anonymisation to prevent
                        re-identification.
                    </p>

                    <h3>Data security and hosting</h3>
                    <p>
                        Pseudonymised research data is hosted on Amazon Web
                        Services (AWS) in the EU or UK to comply with data
                        residency requirements. AWS processes this data on our
                        behalf under a Data Processing Addendum that satisfies
                        the requirements of Article 28 of the UK GDPR, covering
                        documented instructions, confidentiality, security
                        measures, sub-processor controls, data subject rights
                        assistance, breach notification, end-of-contract
                        deletion, and audit rights. AWS is the sole third-party
                        processor of platform data.
                    </p>
                    <p>
                        Organisation-level credentials (API tokens, Moodle web-service
                        tokens) are encrypted at rest using Fernet symmetric encryption
                        (AES-128-CBC with HMAC-SHA256). All data in transit is encrypted
                        via HTTPS/TLS.
                    </p>
                    <p>
                        Student authentication uses short-lived JSON Web Tokens (JWTs)
                        signed with per-organisation HMAC-SHA256 keys. Launch tokens expire
                        in 300 seconds; subsequent tokens expire in 30 seconds.
                    </p>

                    <h3>Data retention</h3>
                    <p>
                        Research data is retained for the duration of the PhD project plus
                        the retention period defined by the University of St&nbsp;Andrews'
                        research data policy (minimum 10 years from the date of any
                        publication based on the data, unless otherwise required).
                    </p>
                    <p>
                        Pseudonymised dataset snapshots may be published alongside study
                        publications via the University's research-data repository and/or
                        Zenodo, subject to ethical approval and participant consent. Only
                        pseudonymised data would be published. Any identifiable mapping data
                        held on university systems will be destroyed in accordance with
                        university policy and the terms of the relevant ethics approval.
                    </p>

                    <h3>Consent withdrawal and deletion</h3>
                    <p>
                        A student may withdraw consent at any time, either within the
                        platform or by contacting the researcher. Withdrawal triggers an
                        application-layer cascade delete of all data associated with that
                        participant across all related tables. We do not retain any data
                        after consent withdrawal.
                    </p>

                    <h3>Institutional data sharing agreements</h3>
                    <p>
                        Each participating institution is an independent data controller
                        for the personal data it holds in Moodle. Before any student data
                        is collected, the institution and the researcher enter into a data
                        sharing agreement that defines the scope of data collection, each
                        party's controller responsibilities, retention periods, and the
                        process for handling data subject requests that require
                        coordination between the two systems.
                    </p>
                </section>

                {/* ================================================================
                    COOKIES
                    ================================================================ */}
                <section className="privacy-section">
                    <h2>Cookies</h2>

                    <h3>Website cookies</h3>
                    <p>
                        Google Analytics sets cookies (including <code>_ga</code> and{' '}
                        <code>_ga_*</code>) to distinguish unique visitors and track
                        session activity. These cookies expire after up to 2 years.
                        IP addresses are anonymised before processing.
                    </p>
                    <p>
                        HubSpot may set a tracking
                        cookie (<code>hubspotutk</code>) in your browser when you interact
                        with the contact form. If present, this cookie value is sent with
                        your form submission to associate it with your HubSpot contact
                        record.
                    </p>

                    <h3>Platform cookies</h3>
                    <p>
                        The SRL Advisor platform sets an HttpOnly secure session cookie
                        after authentication. This cookie has a 4-hour lifetime and is used
                        solely to maintain your authenticated session. It contains no
                        personal information.
                    </p>
                </section>

                {/* ================================================================
                    YOUR RIGHTS
                    ================================================================ */}
                <section className="privacy-section">
                    <h2>Your rights</h2>
                    <p>
                        Under the UK GDPR and EU GDPR, you may have the right to access,
                        rectify, erase, or port your data, or to object to or restrict its
                        processing. For website enquiries or platform data requests,
                        contact{' '}
                        <a href="mailto:hello@srladvisor.com">hello@srladvisor.com</a>.
                        Platform participants can also withdraw consent directly within the
                        tool.
                    </p>
                    <p>
                        Because the SRL Advisor platform stores only pseudonymised data,
                        access or deletion requests for platform data may require
                        coordination with the participant's institution to confirm identity
                        via the Moodle pseudonym mapping.
                    </p>
                </section>

                <section className="privacy-section">
                    <h2>Changes to this statement</h2>
                    <p>
                        We may update this statement from time to time. Material changes
                        will be noted by updating the effective date at the top of this
                        page.
                    </p>
                </section>

                <section className="privacy-section">
                    <h2>Contact</h2>
                    <p>
                        Andrew Schwabe<br />
                        School of Computer Science, University of St&nbsp;Andrews<br />
                        <a href="mailto:hello@srladvisor.com">hello@srladvisor.com</a>
                    </p>
                </section>
            </Container>
        </main>
    );
}

export default PrivacyPolicy;
